import * as THREE from "three";
import debug from "debug";
import MakeGrid from "./MakeGrid";
import MakeLines from "./MakeLines";

const log = debug("app:walls");

/**
 * Class to manage the creation and placement of walls based on grid and line data.
 * @category Experience
 * @subcategory GeometryManagement
 */
class MakeWalls {
  /**
   * Constructs the MakeWalls instance.
   * @param {Object} experience - The main application experience object.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = this.experience.scene;
    this.eventHub = this.experience.eventHub;
    this.wallConfig = [
      { name: "XM-WT1-0601", length: 6 },
      { name: "XM-WT1-0501", length: 5 },
      { name: "XM-WT1-0401", length: 4 },
      { name: "XM-WT1-0301", length: 3 },
    ];
    log("MakeWalls initialized");
    this.setEventListeners();
  }

  /**
   * Sets up event listeners for the walls functionality.
   */
  setEventListeners() {
    this.eventHub.on("walls-switch", this.init.bind(this));
  }

  cleanup() {
    log("Cleaning up MakeWalls instance");

    // Remove event listeners
    this.eventHub.off("walls-switch", this.init.bind(this));

    // Clear all walls
    this.clearWalls();

    // Remove references to prevent memory leaks
    this.experience = null;
    this.scene = null;
    this.eventHub = null;

    log("MakeWalls cleanup completed.");
  }

  /**
   * Initializes the wall creation process.
   * @param {Object} event - The event object containing the enabled state.
   */
  init(event) {
    if (!event.enabled) {
      this.clearWalls();
      log("Walls calculation disabled");
      return;
    }

    // Step 1: Traverse the scene and check for the required object
    let objectFound = false;
    this.scene.traverse((object) => {
      if (object.name === "XMO" && object.userData.layer === 35) {
        objectFound = true;
      }
    });

    // Step 2: If object is not found, block further execution, show modal, and set toggle back to false
    if (!objectFound) {
      // Emit modal event to show the modal
      this.eventHub.emit("modalInfo", {
        title: "Probes Missing",
        message:
          "There are no probes present in this design. You need to add probes in order to add walls.",
        actions: [
          {
            label: "OK",
            callback: () => {
              // Set the toggle switch back to false when user clicks OK
              const wallsSwitch = document.getElementById("walls-switch");
              if (wallsSwitch) {
                wallsSwitch.checked = false; // Set it to false
                // Optionally, emit event to handle switch change
                this.eventHub.emit("walls-switch", { enabled: false });
              }
            },
          },
        ],
      });

      return; // Stop further execution of the walls class
    }

    // If object is found, continue with wall creation
    this.clearWalls();
    const makeGrid = new MakeGrid(this.experience);
    const gridData = makeGrid.init();

    const makeLines = new MakeLines(this.experience);
    const lines = makeLines.init(gridData);
    const wallXMDs = this.createWallsFromLines(lines);

    this.eventHub.emit("MakePartsEvent", {
      eventType: "add",
      data: {
        figures: wallXMDs,
        version: "2.0",
      },
    });
  }

  /**
   * Creates walls from the given line data.
   * @param {Array} lines - An array of line objects containing start position, length, and angle.
   * @returns {Array} - An array of wall objects created from the lines.
   */
  createWallsFromLines(lines) {
    const wallXMDs = [];

    lines.forEach((line) => {
      const { s, length, angle } = line;
      let remainingLength = length;
      let currentPosition = s.clone(); // Start from the original position

      try {
        // Fit as many wall segments as possible
        const segments = this.fitWallSegments(remainingLength);

        // For each wall segment, create and position the wall
        segments.forEach((segmentLength, index) => {
          const config = this.wallConfig.find(
            (c) => c.length === segmentLength
          );
          if (config) {
            wallXMDs.push(
              this.createWallSegment(config.name, currentPosition, angle)
            );

            // Correctly update the position for the next segment
            log(
              `Before update: (${currentPosition.x}, ${currentPosition.z}), length: ${segmentLength}, angle: ${angle}`
            );
            currentPosition = this.updatePosition(
              currentPosition,
              config.length,
              angle
            );
            log(
              `After update: (${currentPosition.x}, ${currentPosition.z}), next segment starts here`
            );
          }
        });
      } catch (error) {
        console.error(`Error creating wall segments: ${error.message}`);
      }
    });

    return wallXMDs;
  }

  // Helper function to fit wall segments optimally

  /**
   * Fits wall segments to the given length.
   * @param {number} remainingLength - The total length available for fitting wall segments.
   * @returns {Array} - An array of segment lengths that fit the total length.
   * @throws {Error} If the remaining length is less than the smallest segment size (3).
   */
  fitWallSegments(remainingLength) {
    const wallSegments = [];

    // Only throw an error if the initial length is less than 3
    if (remainingLength < 3) {
      throw new Error(
        `Line too small to fit a wall, total length: ${remainingLength}`
      );
    }

    const segmentSizes = [6, 5, 4, 3];

    // Helper function for recursive segment fitting
    function findSegments(remainingLength, segments) {
      if (remainingLength === 0) return true; // Success case
      if (remainingLength < 3) return false; // Failure case

      for (const size of segmentSizes) {
        if (remainingLength >= size) {
          segments.push(size); // Try this segment
          if (findSegments(remainingLength - size, segments)) {
            return true; // Success, solution found
          }
          segments.pop(); // Backtrack, remove the segment
        }
      }

      return false;
    }

    // Start the recursive search for segments
    findSegments(remainingLength, wallSegments);

    return wallSegments;
  }

  // Existing method for creating a wall segment

  /**
   * Creates a single wall segment.
   * @param {string} name - The name of the wall part.
   * @param {THREE.Vector3} position - The starting position of the wall segment.
   * @param {number} angle - The angle of the wall segment in degrees.
   * @returns {Object} - A wall object with part data and position details.
   */
  createWallSegment(name, position, angle) {
    return {
      partData: {
        mode: "xm-part",
        fullpartnumber: name,
        layer: 33,
        parent: "no result",
      },
      x: position.x,
      y: position.y ?? 0,
      z: position.z,
      angle: angle,
    };
  }


  // Existing method to update position after placing a wall segment

  /**
   * Updates the position for the next wall segment based on length and angle.
   * @param {THREE.Vector3} position - The current position.
   * @param {number} length - The length of the segment to add.
   * @param {number} angle - The angle of the segment in degrees.
   * @returns {THREE.Vector3} - The updated position.
   */
  updatePosition(position, length, angle) {
    const radianAngle = THREE.MathUtils.degToRad(angle);
    return new THREE.Vector3(
      position.x + Math.cos(radianAngle) * length,
      0,
      position.z - Math.sin(radianAngle) * length
    );
  }

  /**
   * Clears all existing wall objects from the scene.
   */
  clearWalls() {
    const toRemove = [];
    this.scene.traverse((object) => {
      if (object.name === "XMO" && object.userData.layer === 33) {
        toRemove.push(object);
      }
    });
    toRemove.forEach((object) => {
      this.scene.remove(object);
    });
    this.eventHub.emit("xmd");
    log("Cleared previous wall outlines.");
  }
}

export default MakeWalls;
